import React, { useState, useEffect, useRef } from 'react'
import _ from 'lodash'
import styled from 'styled-components'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation, Trans } from 'react-i18next'
import { breakpoints, colors } from 'globalStyles'
import { Card } from 'react-bootstrap'
import { ReactComponent as CalendarDays } from 'assets/svg/calendar-days.svg'
import { ReactComponent as CircleCheck } from 'assets/svg/circle-check-unfilled.svg'
import { ReactComponent as LocationDot } from 'assets/svg/location-dot.svg'
import { ReactComponent as SquareExclamation } from 'assets/svg/square-exclamation.svg'
import { Row } from 'reusableComponents/Cards'
import Tooltip from 'reusableComponents/ToolTip'
import { parseISO, format } from 'date-fns'
import { enCA, fr } from 'date-fns/locale'
import Button from 'react-bootstrap/Button'
import { setShipmentPreferences } from 'store/shipping/shippingSlice'
import {
  selectIsUserVerified,
  selectDeliverySummary,
  selectExpressChequeFlags,
  selectEstimatedDeliveryDate,
  selectAddressCorrectionInfo,
  selectShippingInfo,
  selectPackageStatus,
  selectAllowFlagsForPreferences,
  selectIsLeadPinDelivered,
  selectDeliveryDateTitleTranslationsKey,
} from 'store/shipping/shippingSelectors'
import { selectVirtualAgentUrl } from 'store/global/globalSelectors'
import Verification from 'components/Verification'
import SuccessModal from 'components/SuccessModal'
import PhotoPOD from 'components/PhotoPod'
import SignatureReleasePopUp from 'components/SignaturePreference/SignatureReleasePopUp'
import constants from 'constants.json'
import BuzzerCodePopUp from './BuzzerCodePopUp'
import DeliveryAddressPopUp from './DeliveryAddressPopUp'

const StyledBaseCard = styled(Card)`
  display: flex;
  flex-direction: row !important;
  position: relative;
  padding: 0px;
  border-radius: 8px 0px;
  width: 100%;
  font-size: 14px;
  .card-body {
    display: block;
  }
  @media only screen and ${breakpoints.desktop} and ${breakpoints.tablet} {
    padding: 24px;
  }
  @media only screen and ${breakpoints.desktop} {
    font-size: 16px;
  }
`
const StyledSpan = styled.span`
  font-weight: 600;
  display: table-cell;
  width: 90%;
  position: relative;
  vertical-align: top;
  color: ${colors.primary_blue};
`

const StyledRow = styled.div`
  margin-top: 10px;
  margin-bottom: 20px;

  .hfpu-link-tooltip {
    margin-top: -8px;
    display: flex;
    align-items: center;
    margin-left: 3px;
    gap: 2px;
  }
  .hfpu-link {
    margin-top: 0px;
    font-size: 14px;

    @media only screen and ${breakpoints.desktop} {
      font-size: 16px;
    }
  }
`

const ViewDelAddressLink = styled(Button)`
  margin-top: -14px;
  margin-left: 35px;
  font-size: 14px !important;
  line-height: 20px;

  @media only screen and ${breakpoints.desktop} {
    font-size: 16px !important;
  }
`

const ViewBuzzCodeLink = styled(Button)`
  margin-top: 0px;
  margin-left: 35px;
  font-size: 14px !important;
  line-height: 20px;

  @media only screen and ${breakpoints.desktop} {
    font-size: 16px !important;
  }
`

const ViewHFPUAddressLink = styled(Button)`
  margin-top: -8px;
  margin-left: 32px;
  font-size: 14px;
  line-height: 20px;
`

const YellowCard = styled.div`
  width: 100%;
  min-height: 40px;
  background-color: ${colors.statusPickedUp};
  border: 1px solid ${colors.web_grey};
  border-radius: 4px;
  padding: 10px 12px;
  gap: 12px;
  margin-top: 16px;
  display: flex;
  align-items: flex-start;
`

const GreenCard = styled.div`
  width: 100%;
  min-height: 40px;
  background-color: ${colors.statusDelivered};
  border: 1px solid ${colors.web_grey};
  border-radius: 4px;
  padding: 10px 12px;
  gap: 12px;
  margin-top: 16px;
  display: flex;
  align-items: flex-start;
  opacity: 1;
  transform: rotate(0deg);

  @media only screen and ${breakpoints.desktop} {
    align-items: center;
  }
`

const StyledExclamation = styled(SquareExclamation)`
  min-width: 18px;
  width: 18px;
  height: 18px;
  opacity: 1;
  margin-top: 2px;

  @media only screen and ${breakpoints.desktop} {
    margin-top: 0;
  }
`

const StyledCircleCheck = styled(CircleCheck)`
  min-width: 18px;
  width: 18px;
  height: 18px;
  opacity: 1;
  transform: rotate(0deg);
`

const YellowCardText = styled.strong`
  flex: 1;
  opacity: 1;
  font-size: 14px;

  @media only screen and ${breakpoints.desktop} {
    width: 510px;
    line-height: 20px;
    font-size: 16px;
  }
`

const GreenCardText = styled.span`
  flex: 1;
  opacity: 1;
  font-size: 14px;
  transform: rotate(0deg);

  @media only screen and ${breakpoints.desktop} {
    width: 250px;
    height: 40px;
  }
`

const SignatureMessage = styled.p`
  width: 100%;
  min-height: 20px;
  opacity: 1;
  font-size: 14px;
  line-height: 1.4;
  margin-top: 12px;
  margin-bottom: 0;

  @media only screen and ${breakpoints.desktop} {
    line-height: 20px;
    font-size: 16px;
  }
`

const SignatureLink = styled(Button)`
  padding: 0;
  font-size: 14px;
  line-height: 1.4;
  text-decoration: underline;
  text-decoration-style: solid;
  color: ${colors.text_link};
  cursor: pointer;
  margin-bottom: 4px;
  white-space: normal;
  text-align: left;
  height: auto;

  &:hover {
    color: ${colors.text_link_hover};
    text-decoration-thickness: 2px;
  }

  @media only screen and ${breakpoints.desktop} {
    line-height: 20px;
    white-space: nowrap;
    font-size: 16px;
  }
`

const SignatureDoneLink = styled.div`
  width: 100%;
  min-height: 24px;
  opacity: 1;
  margin-top: 12px;
  color: ${colors.primary_black};
  background: transparent;
  border: none;
  padding: 0;
`

const UnderlinedLink = styled(Button)`
  padding: 0;
  text-decoration: underline;
  text-decoration-style: solid;
  color: ${colors.text_link};
  cursor: pointer;
`

const PurolatorLink = styled.a`
  padding: 0;
  font-size: 14px;
  line-height: 1.4;
  text-decoration: underline;
  text-decoration-style: solid;
  color: ${colors.text_link};
  cursor: pointer;
  background: none;
  border: none;
  margin-bottom: 4px;

  &:hover {
    color: ${colors.text_link_hover};
    text-decoration-thickness: 2px;
  }

  @media only screen and ${breakpoints.desktop} {
    line-height: 20px;
    font-size: 16px;
  }
`

const StyledDateSpan = styled.span`
  font-weight: 600;
  color: ${colors.primary_black};
  font-size: 20px;
  display: inline-flex;

  @media only screen and ${breakpoints.desktop} {
    font-size: 26px;
  }

  .date-seperator {
    color: ${colors.web_grey};
    margin: 0 4px;
    font-size: 20px;
    @media only screen and ${breakpoints.desktop} {
      font-size: 26px;
      margin: 0 6px;
    }
  }

  .time {
    font-weight: 400;
  }
`

const StyledAddressSpan = styled.span`
  font-weight: 400;
  color: ${colors.primary_black};
  font-size: 14px;
  line-height: 20px;

  @media only screen and ${breakpoints.desktop} {
    font-size: 16px;
  }

  .semibold-address {
    font-weight: 600 !important;
  }

  .pickup-label {
    display: block;
    margin-top: 5px;
  }

  .pickup-address {
    display: block;
    font-weight: 600;
  }
`

const StyledDiv = styled.div`
  @media only screen and ${breakpoints.mobile} and ${breakpoints.tablet} {
    line-height: 20px;
    font-size: 14px;
  }
`

const PhotoPODDiv = styled.div`
  margin-top: 20px;
`

const StyledAddressValue = styled.span`
  line-height: 20px;
  font-size: 14px;
  font-weight: 600;
  @media only screen and ${breakpoints.desktop} {
    line-height: 24px;
    font-size: 16px !important;
  }
`

const locales = { en: enCA, fr }

const formatDateTime = (dateTime, lng) => {
  if (!dateTime) return ''
  // Regex to check for time part (HH:MM or T)
  const hasTime = /\d{2}:\d{2}|T\d{2}:\d{2}/.test(dateTime)
  const parsedDate = parseISO(dateTime.split('.')[0]) // Remove milliseconds if present
  const locale = locales[lng]
  const dayOfWeek = format(parsedDate, 'EEEE', { locale })
  const monthDay = format(parsedDate, 'MMMM d', { locale })
  if (!hasTime) {
    // Only date, display without time
    return `${dayOfWeek}, ${monthDay}`
  }
  const time = format(parsedDate, 'h:mma', { locale }).toLowerCase()
  return (
    <>
      {`${dayOfWeek}, ${monthDay}`}
      <span className="date-seperator">|</span>
      <span className="time">{time}</span>
    </>
  )
}

const formatEstimatedDeliveryDate = (deliveryDate, t, lng) => {
  const { DELIVERY_DATE_RECALCULATING } = constants
  const { BUSINESS_DELIVERY_DATE } = constants
  let content = ''
  if (!deliveryDate || deliveryDate === DELIVERY_DATE_RECALCULATING) {
    content = (
      <Trans
        t={t}
        i18nKey="tracking.not_available"
        components={[
          <Tooltip
            key="tooltip-edd-not-available"
            id="tootip-edd-not-available"
            placement="bottom"
          />,
        ]}
      />
    )
  } else if (deliveryDate.includes(BUSINESS_DELIVERY_DATE)) {
    content = deliveryDate.replace(BUSINESS_DELIVERY_DATE, t('tracking.business_days'))
  } else {
    content = formatDateTime(deliveryDate, lng)
  }
  return <StyledDateSpan>{content}</StyledDateSpan>
}

const addressBuilder = (address) => {
  const addressOne = _.chain(address).get('address', '').lowerCase().startCase().value()
  const postalcode = _.get(address, 'postalcode', '')
  const province = _.get(address, 'province', '')
  const city = _.chain(address).get('city', '').lowerCase().startCase().value()

  const addressArray = _.compact([addressOne, city, province, postalcode])

  return addressArray.reduce((acc, curr, i) => {
    if (i === addressArray.length - 1) {
      return `${acc}${curr}`
    }
    return `${acc}${curr}, `
  }, '')
}

const DeliveryDateAddrCorrection = () => {
  const { t, i18n } = useTranslation()
  const dispatch = useDispatch()
  const [showPopUp, setShowPopUp] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [showVerificationPopUp, setShowVerificationPopUp] = useState(false)
  const [pendingAction, setPendingAction] = useState(() =>
    sessionStorage.getItem('pendingPrefAction')
  )
  const allowFlagsForPreferences = useSelector(selectAllowFlagsForPreferences)
  const [showSignatureReleasePopUp, setShowSignatureReleasePopUp] = useState(false)
  const [pendingSignatureAction, setPendingSignatureAction] = useState(null)
  const isVerifiedUser = useSelector(selectIsUserVerified)
  const shippingInfo = useSelector(selectShippingInfo)
  const packageStatus = useSelector(selectPackageStatus)
  const isLeadPinDelivered = useSelector(selectIsLeadPinDelivered)
  const titleTranslationKey = useSelector(selectDeliveryDateTitleTranslationsKey)
  const isDelivered = packageStatus === constants.STATUS.DELIVERED

  const {
    deliveryLocation,
    buzzerCode,
    additionalDeliveryInstructions,
    remoteSignaturePreference,
    sr,
    asr,
    cos,
    snr,
    dgFlag,
    dgMandatoryFlag,
  } = useSelector(selectDeliverySummary)
  const { expressChequeFlag, expressChequeMOP, expressChequeAmount } =
    useSelector(selectExpressChequeFlags)

  const getYellowCardMessage = () => {
    if (dgFlag) return t('yellow_card.dg_required_message')
    if (asr) return t('yellow_card.asr_required')
    if (cos) return t('yellow_card.cos_required')
    if (snr) return t('yellow_card.snr_required_message')
    if (sr) return t('yellow_card.sr_required_message')
    return ''
  }

  const getExpressChequeMessage = () => {
    if (expressChequeFlag) {
      const formatOfPayment = expressChequeMOP?.replaceAll(' ', '_')
      return t('expressCheque.express_cheque_text', {
        ecMOP: t(`expressCheque.${formatOfPayment}`),
        ecAmount: expressChequeAmount,
      })
    }
    return null
  }

  const [buzCode, setBuzCode] = useState(buzzerCode)
  const [popUpType, setPopUpType] = useState('buzzerCode')
  const [successValue, setSuccessValue] = useState({
    buzzerCodeVal: '',
  })
  const [successMessage, setSuccessMessage] = useState('')
  // Delivery date fields
  const estimateDeliveryDate = useSelector(selectEstimatedDeliveryDate)
  const displayEstimateDeliveryDate = formatEstimatedDeliveryDate(
    estimateDeliveryDate,
    t,
    i18n.language
  )
  // Address correction fields
  const virtualAgentUrl = useSelector(selectVirtualAgentUrl)
  const addressCorrectionInfo = useSelector(selectAddressCorrectionInfo)
  const {
    shipmentPin,
    toAddress,
    requestedAddressCorrection,
    allowAddressCorrection,
    hasAddressCorrectionException,
    holdForPickUp,
    addressCorrectionRequest,
  } = addressCorrectionInfo
  const { hasHFPUCompleted, hasHFPURequested, hasReachedRetailLocation } = allowFlagsForPreferences
  const addressCorrectionURL = `${virtualAgentUrl}&intent=Address.Modify&pin=${shipmentPin}`
  const hfpuURL = `${virtualAgentUrl}&intent=Shipment.Hold&pin=${shipmentPin}`
  const addressCorrectionStatus = _.get(requestedAddressCorrection, 'addressCorrectionStatus', null)
  const ADDRESS_CORRECTION_COMPLETED = constants.ADDRESS_CORRECTION.COMPLETED
  const ADDRESS_CORRECTION_IN_PROGRESS = constants.ADDRESS_CORRECTION.IN_PROGRESS
  const addressContent =
    addressCorrectionStatus === ADDRESS_CORRECTION_COMPLETED && requestedAddressCorrection
      ? addressBuilder(requestedAddressCorrection)
      : addressBuilder(toAddress)
  const hfpuAddressContent = () => {
    if (isVerifiedUser) {
      const addrContent = addressBuilder(holdForPickUp)
      const idx = addrContent.indexOf(',')
      const addrList =
        idx === -1 ? [addrContent] : [addrContent.slice(0, idx), addrContent.slice(idx + 1).trim()]
      return (
        <>
          <span className="semibold-address pickup-address">{addrList[0]}</span>
          {addrList[1] && <span className="semibold-address pickup-address">{addrList[1]}</span>}
        </>
      )
    }
    return <span className="semibold-address">{addressBuilder(toAddress)}</span>
  }

  const timeoutRef = useRef(null)
  const { photoPODImages = [] } = shippingInfo
  const hasPhotoPOD = photoPODImages.length > 0

  useEffect(() => {
    // Cleanup function to clear the timeout when the component unmounts
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const performAction = (popupType) => {
    if (popupType === 'signatureRelease') {
      setShowSignatureReleasePopUp(true)
    } else {
      setShowPopUp(true)
      setPopUpType(popupType)
    }
  }

  useEffect(() => {
    if (!pendingAction) return
    if (isVerifiedUser) {
      performAction(pendingAction)
    } else {
      setShowVerificationPopUp(true)
    }
  }, [isVerifiedUser, pendingAction, performAction])

  useEffect(() => {
    // Handle verification completion - if user just became verified and we have a pending signature action
    if (isVerifiedUser && pendingSignatureAction === 'signatureRelease') {
      setShowSignatureReleasePopUp(true)
      setPendingSignatureAction(null)
    }
    if (isVerifiedUser && pendingSignatureAction === 'hfpu') {
      window.open(hfpuURL, '_blank', 'noreferrer')
      setPendingSignatureAction(null)
    }
  }, [isVerifiedUser, pendingSignatureAction, hfpuURL])

  const verificationPopUPClickHandler = (popupType) => {
    if (isVerifiedUser) {
      performAction(popupType)
    } else {
      setPendingAction(popupType)
      // Store popupType directly in sessionStorage
      sessionStorage.setItem('pendingPrefAction', popupType)
      if (popupType === 'signatureRelease') {
        setPendingSignatureAction('signatureRelease')
      }
      setShowVerificationPopUp(true)
    }
  }

  const clearSessionStorage = () => {
    setPendingAction(null)
    sessionStorage.removeItem('pendingPrefAction')
  }

  const handleHFPUClick = () => {
    if (isVerifiedUser) {
      window.open(hfpuURL, '_blank', 'noreferrer')
      clearSessionStorage()
    } else {
      setPendingSignatureAction('hfpu')
      setShowVerificationPopUp(true)
    }
  }

  const onHidePopUp = () => {
    setShowPopUp(false)
    clearSessionStorage()
  }

  const onSaveDeliveryPreferences = (buzzCode) => {
    clearSessionStorage()
    dispatch(
      setShipmentPreferences(
        additionalDeliveryInstructions,
        buzzCode,
        deliveryLocation,
        remoteSignaturePreference
      )
    )
    switch (popUpType) {
      case 'buzzerCode': {
        setSuccessValue({ ...successValue, buzzerCodeVal: buzzCode })
        if (buzCode) {
          // If buzzerCode is updated
          setSuccessMessage(t('del_date_addr_correction.successMsgUpdate'))
        } else {
          // If buzzerCode is added
          setSuccessMessage(t('del_date_addr_correction.successMsg'))
        }
        setShowModal(true)
        // Store the timeout ID in the ref
        timeoutRef.current = setTimeout(() => {
          setShowModal(false)
        }, 3000)
        setBuzCode(buzzCode)
        break
      }
      default:
        break
    }
  }

  const cancelModal = () => {
    setShowModal(false)
  }

  const getAddCorrectionContent = () => {
    if (isVerifiedUser && allowAddressCorrection) {
      return t('del_date_addr_correction.only_change_del_addr')
    }
    if (!isVerifiedUser && hasAddressCorrectionException) {
      return t('del_date_addr_correction.view_del_addr')
    }
    if (!isVerifiedUser) {
      return t('del_date_addr_correction.change_del_addr')
    }
    return null
  }

  // Group repeated conditions to avoid duplication
  const hasSpecialRequirements = asr || cos || dgMandatoryFlag || snr || expressChequeFlag
  const noSpecialRequirements = !hasSpecialRequirements

  return (
    <StyledBaseCard>
      <Card.Body>
        <StyledSpan> {t(titleTranslationKey)}</StyledSpan>
        <StyledRow>
          <Row
            svgIcon={{
              Icon: CalendarDays,
              color: colors.secondary_light_blue,
            }}
          >
            {displayEstimateDeliveryDate}
          </Row>
        </StyledRow>
        {hasHFPURequested || hasHFPUCompleted ? (
          <StyledRow>
            <Row
              svgIcon={{
                Icon: LocationDot,
                color: colors.secondary_light_blue,
              }}
            >
              <StyledAddressSpan>
                <span className="pickup-label">{t('set_preferences.pick_up_location')}</span>
                {hfpuAddressContent()}
              </StyledAddressSpan>
            </Row>
            {!isVerifiedUser && (
              <div className="hfpu-link-tooltip">
                <ViewHFPUAddressLink
                  variant="link"
                  className="hfpu-link"
                  onClick={() => {
                    setShowVerificationPopUp(true)
                  }}
                  data-tooltip-id="tooltip-view-pick-up-location"
                >
                  {t('set_preferences.view_pk_location')}
                </ViewHFPUAddressLink>
                <Tooltip id="tooltip-view-pick-up-location" placement="bottom" />
              </div>
            )}
          </StyledRow>
        ) : (
          <>
            <StyledRow>
              <Row
                svgIcon={{
                  Icon: LocationDot,
                  color: colors.secondary_light_blue,
                }}
              >
                <StyledAddressSpan>
                  <Trans
                    i18nKey={
                      isLeadPinDelivered
                        ? 'set_preferences.delivered_to_address'
                        : 'set_preferences.deliver_to_address'
                    }
                    components={[<StyledAddressValue />]}
                    values={{ address: addressContent }}
                  />
                  {isLeadPinDelivered && buzCode && isVerifiedUser && (
                    <StyledDiv>{`${t(
                      'del_date_addr_correction.buzzer_code'
                    )}: ${buzCode}`}</StyledDiv>
                  )}
                </StyledAddressSpan>
              </Row>
            </StyledRow>
            <ViewDelAddressLink
              variant="link"
              onClick={() => verificationPopUPClickHandler('viewDelAddr')}
              style={{ marginBottom: '8px', display: 'block' }}
            >
              {getAddCorrectionContent()}
            </ViewDelAddressLink>
            {!isLeadPinDelivered && (
              <ViewBuzzCodeLink
                variant="link"
                onClick={() => verificationPopUPClickHandler('buzzerCode')}
              >
                {buzCode
                  ? `${t('del_date_addr_correction.buzzer_code')}: ${buzCode}`
                  : t('del_date_addr_correction.add_buzz_code')}
              </ViewBuzzCodeLink>
            )}
          </>
        )}
        {hasPhotoPOD && isDelivered && (
          <PhotoPODDiv>
            <PhotoPOD photoPODImages={photoPODImages} />
          </PhotoPODDiv>
        )}
        {requestedAddressCorrection &&
          addressCorrectionStatus === ADDRESS_CORRECTION_IN_PROGRESS &&
          !isDelivered && (
            <GreenCard>
              <StyledCircleCheck />
              <GreenCardText>{t('address_validation.address_correction_recieved')}</GreenCardText>
            </GreenCard>
          )}
        {requestedAddressCorrection &&
          addressCorrectionStatus === ADDRESS_CORRECTION_COMPLETED &&
          !isDelivered && (
            <YellowCard>
              <StyledCircleCheck />
              <YellowCardText>
                {t('address_validation.update_address_request_success')}
              </YellowCardText>
            </YellowCard>
          )}
        {hasHFPUCompleted && hasHFPURequested && !hasReachedRetailLocation && !isDelivered && (
          <GreenCard>
            <StyledCircleCheck />
            <GreenCardText>{t('hfpu_component.update_address_request_success')}</GreenCardText>
          </GreenCard>
        )}
        {hasHFPUCompleted && hasHFPURequested && hasReachedRetailLocation && !isDelivered && (
          <GreenCard>
            <StyledCircleCheck />
            <GreenCardText>{t('hfpu_component.retail_location')}</GreenCardText>
          </GreenCard>
        )}
        {hasHFPURequested && !hasHFPUCompleted && !isDelivered && (
          <YellowCard>
            <StyledExclamation />
            <YellowCardText>{t('hfpu_component.address_correction_recieved')}</YellowCardText>
          </YellowCard>
        )}
        {(hasSpecialRequirements || sr || dgFlag) && !isDelivered && (
          <>
            <YellowCard>
              <StyledExclamation />
              <YellowCardText>
                {expressChequeFlag ? getExpressChequeMessage() : getYellowCardMessage()}
              </YellowCardText>
            </YellowCard>
            {((sr && remoteSignaturePreference !== 'PTL' && noSpecialRequirements) ||
              !addressCorrectionRequest) &&
              !hasHFPURequested && (
                <SignatureMessage>
                  {t('yellow_card.away_during_delivery')}
                  {sr &&
                    remoteSignaturePreference !== 'PTL' &&
                    noSpecialRequirements &&
                    (addressCorrectionRequest ? (
                      <SignatureLink
                        variant="link"
                        onClick={() => verificationPopUPClickHandler('signatureRelease')}
                      >
                        {t('yellow_card.skip_the_signature')}
                      </SignatureLink>
                    ) : (
                      <Trans
                        t={t}
                        i18nKey="yellow_card.skip_the_signature_with_or"
                        components={[
                          <SignatureLink
                            variant="link"
                            onClick={() => verificationPopUPClickHandler('signatureRelease')}
                          />,
                        ]}
                      />
                    ))}

                  {!addressCorrectionRequest && (
                    <PurolatorLink onClick={handleHFPUClick}>
                      {t('yellow_card.send_to_purolator')}
                    </PurolatorLink>
                  )}
                </SignatureMessage>
              )}
          </>
        )}
        {remoteSignaturePreference === 'PTL' &&
          noSpecialRequirements &&
          !isDelivered &&
          !hasHFPURequested && (
            <SignatureDoneLink>
              {t('yellow_card.sign_set')}
              <UnderlinedLink
                variant="link"
                onClick={() => verificationPopUPClickHandler('signatureRelease')}
              >
                {t('yellow_card.undo')}
              </UnderlinedLink>
            </SignatureDoneLink>
          )}
        <Verification
          show={showVerificationPopUp}
          onHide={(closeValue) => {
            setShowVerificationPopUp(closeValue)
            // If user cancels verification, clear pending action
            if (closeValue === false) {
              clearSessionStorage()
            }
          }}
        />
        {popUpType === 'buzzerCode' && (
          <BuzzerCodePopUp
            show={showPopUp}
            onHide={() => {
              setShowPopUp(false)
              clearSessionStorage()
            }}
            onSave={onSaveDeliveryPreferences}
          />
        )}
        <SignatureReleasePopUp
          show={showSignatureReleasePopUp}
          onHide={() => {
            setShowSignatureReleasePopUp(false)
            clearSessionStorage()
          }}
          popUpType="signAuth"
        />
        {popUpType === 'viewDelAddr' && (
          <DeliveryAddressPopUp
            show={showPopUp}
            onHide={onHidePopUp}
            address={addressContent}
            allowAddressCorrection={allowAddressCorrection}
            addressCorrectionRequest={addressCorrectionRequest}
            onIncorrectAddressClick={() => {
              window.open(addressCorrectionURL, '_blank')
              setShowPopUp(false)
            }}
          />
        )}
        <SuccessModal show={showModal} onHide={cancelModal} message={successMessage} />
      </Card.Body>
    </StyledBaseCard>
  )
}

export default DeliveryDateAddrCorrection
